import { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  AlertTriangle, Shield, Info, AlertCircle, Search, Filter,
  RefreshCw, Download, Eye, Monitor, Terminal,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader,
  DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

interface UnifiedLog {
  id: string;
  hostname: string;
  message: string;
  event_time: string;
  source_os: "windows" | "linux";
  // Windows-specific
  event_id?: number;
  log_name?: string;
  level_name?: string;
  provider_name?: string;
  user_sid?: string;
  raw?: any;
  // Linux-specific
  severity?: string;
  log_source?: string;
  process_name?: string;
  user_name?: string;
  unit?: string;
}

const CRITICAL_EVENTS: Record<number, string> = {
  4625: "Failed Logon", 4740: "Account Locked Out", 4720: "Account Created",
  4726: "Account Deleted", 4738: "Account Changed", 4648: "Explicit Credentials Logon",
  4688: "Process Created", 4104: "PowerShell Script Block",
  7031: "Service Terminated Unexpectedly", 7034: "Service Crashed",
  1074: "System Shutdown/Restart", 4624: "Successful Logon",
  4634: "Account Logged Off", 5156: "Network Connection Allowed",
  5157: "Network Connection Blocked",
};

function mapSeverityToLevel(severity: string): string {
  const s = severity?.toLowerCase();
  if (["crit", "emerg", "alert"].includes(s)) return "Critical";
  if (["err", "error"].includes(s)) return "Error";
  if (["warning", "warn"].includes(s)) return "Warning";
  return "Information";
}

const LOG_LEVEL_COLORS = {
  Critical: "destructive", Error: "destructive",
  Warning: "secondary", Information: "default",
} as const;

const LOG_LEVEL_ICONS = {
  Critical: AlertTriangle, Error: AlertCircle,
  Warning: AlertTriangle, Information: Info,
};

export default function LogsPage() {
  const { profile } = useAuth();
  const [logs, setLogs] = useState<UnifiedLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLevel, setSelectedLevel] = useState("all");
  const [selectedHostname, setSelectedHostname] = useState("all");
  const [selectedSource, setSelectedSource] = useState<string>("all");
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [allHostnames, setAllHostnames] = useState<string[]>([]);

  const fetchHostnames = useCallback(async () => {
    if (!profile?.tenant_id) return;
    const [{ data: wh }, { data: lh }] = await Promise.all([
      supabase.from("windows_event_logs").select("hostname").eq("tenant_id", profile.tenant_id).limit(1000),
      supabase.from("linux_logs").select("hostname").eq("tenant_id", profile.tenant_id).limit(1000),
    ]);
    const set = new Set<string>();
    wh?.forEach((r: any) => set.add(r.hostname));
    lh?.forEach((r: any) => set.add(r.hostname));
    setAllHostnames([...set].sort());
  }, [profile?.tenant_id]);

  const fetchLogs = useCallback(async () => {
    if (!profile?.tenant_id) return;
    setLoading(true);
    try {
      const fetchWindows = selectedSource === "all" || selectedSource === "windows";
      const fetchLinux = selectedSource === "all" || selectedSource === "linux";

      const results: UnifiedLog[] = [];

      if (fetchWindows) {
        let wq = supabase
          .from("windows_event_logs")
          .select("id, hostname, event_id, log_name, level_name, provider_name, message, user_sid, event_time, raw")
          .eq("tenant_id", profile.tenant_id)
          .order("event_time", { ascending: false })
          .limit(500);

        if (selectedHostname !== "all") wq = wq.eq("hostname", selectedHostname);
        if (selectedLevel !== "all") wq = wq.eq("level_name", selectedLevel);
        if (searchTerm) wq = wq.or(`message.ilike.%${searchTerm}%,provider_name.ilike.%${searchTerm}%,hostname.ilike.%${searchTerm}%`);

        const { data } = await wq;
        (data || []).forEach((r: any) => results.push({
          id: `w-${r.id}`, hostname: r.hostname, message: r.message,
          event_time: r.event_time, source_os: "windows",
          event_id: r.event_id, log_name: r.log_name, level_name: r.level_name,
          provider_name: r.provider_name, user_sid: r.user_sid, raw: r.raw,
        }));
      }

      if (fetchLinux) {
        let lq = supabase
          .from("linux_logs")
          .select("id, hostname, message, event_time, severity, log_source, process_name, user_name, unit, raw")
          .eq("tenant_id", profile.tenant_id)
          .order("event_time", { ascending: false })
          .limit(500);

        if (selectedHostname !== "all") lq = lq.eq("hostname", selectedHostname);
        if (selectedLevel !== "all") {
          const severityMap: Record<string, string[]> = {
            Critical: ["crit", "emerg", "alert"],
            Error: ["err", "error"],
            Warning: ["warning", "warn"],
            Information: ["info", "notice", "debug"],
          };
          const sevs = severityMap[selectedLevel];
          if (sevs) lq = lq.in("severity", sevs);
        }
        if (searchTerm) lq = lq.or(`message.ilike.%${searchTerm}%,process_name.ilike.%${searchTerm}%,hostname.ilike.%${searchTerm}%`);

        const { data } = await lq;
        (data || []).forEach((r: any) => results.push({
          id: `l-${r.id}`, hostname: r.hostname, message: r.message,
          event_time: r.event_time, source_os: "linux",
          severity: r.severity, log_source: r.log_source,
          process_name: r.process_name, user_name: r.user_name,
          unit: r.unit, raw: r.raw,
          level_name: mapSeverityToLevel(r.severity),
        }));
      }

      results.sort((a, b) => new Date(b.event_time).getTime() - new Date(a.event_time).getTime());
      setLogs(results);
    } catch {
      toast.error("Failed to fetch logs");
    } finally {
      setLoading(false);
    }
  }, [profile?.tenant_id, selectedSource, selectedHostname, selectedLevel, searchTerm]);

  useEffect(() => { fetchHostnames(); }, [fetchHostnames]);
  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchLogs, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchLogs]);

  // Realtime subscriptions
  useEffect(() => {
    if (!profile?.tenant_id) return;
    const ch1 = supabase.channel("logs-win").on("postgres_changes", {
      event: "INSERT", schema: "public", table: "windows_event_logs",
      filter: `tenant_id=eq.${profile.tenant_id}`,
    }, () => { if (autoRefresh) fetchLogs(); }).subscribe();
    const ch2 = supabase.channel("logs-lin").on("postgres_changes", {
      event: "INSERT", schema: "public", table: "linux_logs",
      filter: `tenant_id=eq.${profile.tenant_id}`,
    }, () => { if (autoRefresh) fetchLogs(); }).subscribe();
    return () => { supabase.removeChannel(ch1); supabase.removeChannel(ch2); };
  }, [profile?.tenant_id, autoRefresh, fetchLogs]);

  const getDisplayLevel = (log: UnifiedLog) => log.level_name || "Information";

  const getLevelIcon = (level: string) => {
    const Icon = LOG_LEVEL_ICONS[level as keyof typeof LOG_LEVEL_ICONS] || Info;
    return <Icon className="h-4 w-4" />;
  };

  const getEventDescription = (eventId: number) =>
    CRITICAL_EVENTS[eventId] || `Event ${eventId}`;

  const formatLogTime = (ts: string) => new Date(ts).toLocaleString();

  const exportLogs = () => {
    const csv = [
      ["Time", "OS", "Hostname", "Level", "Source", "Message"].join(","),
      ...logs.map((log) => [
        log.event_time, log.source_os, log.hostname,
        getDisplayLevel(log),
        log.source_os === "windows" ? log.provider_name : log.process_name,
        `"${log.message.replace(/"/g, '""')}"`,
      ].join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `logs-${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Logs exported successfully");
  };

  const windowsCount = logs.filter(l => l.source_os === "windows").length;
  const linuxCount = logs.filter(l => l.source_os === "linux").length;
  const criticalCount = logs.filter(l => getDisplayLevel(l) === "Critical" || getDisplayLevel(l) === "Error").length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Logs</h1>
          <p className="text-muted-foreground">
            Unified log viewer for all Windows and Linux servers
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setAutoRefresh(!autoRefresh)}
            className={autoRefresh ? "bg-green-50 dark:bg-green-950" : ""}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Auto Refresh {autoRefresh ? "ON" : "OFF"}
          </Button>
          <Button variant="outline" size="sm" onClick={exportLogs}>
            <Download className="h-4 w-4 mr-2" />Export CSV
          </Button>
          <Button variant="outline" size="sm" onClick={fetchLogs}>
            <RefreshCw className="h-4 w-4 mr-2" />Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Logs</CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{logs.length.toLocaleString()}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Windows</CardTitle>
            <Monitor className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{windowsCount.toLocaleString()}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Linux</CardTitle>
            <Terminal className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{linuxCount.toLocaleString()}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Critical / Errors</CardTitle>
            <AlertTriangle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">{criticalCount}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search messages..." value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)} className="pl-9" />
            </div>

            <Select value={selectedSource} onValueChange={setSelectedSource}>
              <SelectTrigger><SelectValue placeholder="OS Source" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="windows">Windows</SelectItem>
                <SelectItem value="linux">Linux</SelectItem>
              </SelectContent>
            </Select>

            <Select value={selectedLevel} onValueChange={setSelectedLevel}>
              <SelectTrigger><SelectValue placeholder="Level" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Levels</SelectItem>
                <SelectItem value="Critical">Critical</SelectItem>
                <SelectItem value="Error">Error</SelectItem>
                <SelectItem value="Warning">Warning</SelectItem>
                <SelectItem value="Information">Information</SelectItem>
              </SelectContent>
            </Select>

            <Select value={selectedHostname} onValueChange={setSelectedHostname}>
              <SelectTrigger><SelectValue placeholder="Server" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Servers</SelectItem>
                {allHostnames.map((h) => (
                  <SelectItem key={h} value={h}>{h}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Logs Display */}
      <Card>
        <CardHeader>
          <CardTitle>Log Entries ({logs.length.toLocaleString()})</CardTitle>
          <CardDescription>Real-time logs from all monitored servers</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <RefreshCw className="h-8 w-8 animate-spin" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12">
              <Shield className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-2 text-sm font-semibold">No logs found</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                No logs match your current filters
              </p>
            </div>
          ) : (
            <ScrollArea className="h-[600px]">
              <div className="space-y-2">
                {logs.map((log) => {
                  const level = getDisplayLevel(log);
                  return (
                    <div key={log.id}
                      className="flex items-start gap-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          {getLevelIcon(level)}
                          <Badge variant={LOG_LEVEL_COLORS[level as keyof typeof LOG_LEVEL_COLORS] || "default"}
                            className="shrink-0">{level}</Badge>
                          <Badge variant="outline" className="shrink-0">
                            {log.source_os === "windows" ? (
                              <><Monitor className="h-3 w-3 mr-1" />WIN</>
                            ) : (
                              <><Terminal className="h-3 w-3 mr-1" />LIN</>
                            )}
                          </Badge>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 text-sm flex-wrap">
                            <span className="font-medium">{formatLogTime(log.event_time)}</span>
                            <Separator orientation="vertical" className="h-4" />
                            <span className="text-muted-foreground">{log.hostname}</span>
                            {log.event_id && (
                              <>
                                <Separator orientation="vertical" className="h-4" />
                                <Badge variant="outline" className="shrink-0">
                                  {log.event_id} - {getEventDescription(log.event_id)}
                                </Badge>
                              </>
                            )}
                          </div>
                          <p className="text-sm mt-1 text-muted-foreground line-clamp-2">{log.message}</p>
                          <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                            <span>Source: {log.source_os === "windows" ? log.provider_name : log.process_name || log.log_source}</span>
                            {(log.user_sid || log.user_name) && (
                              <>
                                <Separator orientation="vertical" className="h-3" />
                                <span>User: {log.user_sid || log.user_name}</span>
                              </>
                            )}
                            {log.unit && (
                              <>
                                <Separator orientation="vertical" className="h-3" />
                                <span>Unit: {log.unit}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="ghost" size="sm"><Eye className="h-4 w-4" /></Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-4xl max-h-[80vh]">
                          <DialogHeader>
                            <DialogTitle>Log Details</DialogTitle>
                            <DialogDescription>
                              {log.source_os === "windows" && log.event_id
                                ? `Event ID ${log.event_id} - ${getEventDescription(log.event_id)}`
                                : `${log.source_os.toUpperCase()} - ${log.log_source || log.process_name || "System"}`}
                            </DialogDescription>
                          </DialogHeader>
                          <ScrollArea className="max-h-[60vh]">
                            <div className="space-y-4">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="text-sm font-medium">Time</label>
                                  <p className="text-sm text-muted-foreground">{formatLogTime(log.event_time)}</p>
                                </div>
                                <div>
                                  <label className="text-sm font-medium">Hostname</label>
                                  <p className="text-sm text-muted-foreground">{log.hostname}</p>
                                </div>
                                <div>
                                  <label className="text-sm font-medium">OS</label>
                                  <Badge variant="outline">{log.source_os === "windows" ? "Windows" : "Linux"}</Badge>
                                </div>
                                <div>
                                  <label className="text-sm font-medium">Level</label>
                                  <Badge variant={LOG_LEVEL_COLORS[level as keyof typeof LOG_LEVEL_COLORS] || "default"}>{level}</Badge>
                                </div>
                                {log.event_id && (
                                  <div>
                                    <label className="text-sm font-medium">Event ID</label>
                                    <p className="text-sm text-muted-foreground">{log.event_id}</p>
                                  </div>
                                )}
                                <div>
                                  <label className="text-sm font-medium">Source</label>
                                  <p className="text-sm text-muted-foreground">
                                    {log.source_os === "windows" ? log.provider_name : log.process_name || log.log_source}
                                  </p>
                                </div>
                                {(log.user_sid || log.user_name) && (
                                  <div>
                                    <label className="text-sm font-medium">User</label>
                                    <p className="text-sm text-muted-foreground">{log.user_sid || log.user_name}</p>
                                  </div>
                                )}
                                {log.log_name && (
                                  <div>
                                    <label className="text-sm font-medium">Log Name</label>
                                    <p className="text-sm text-muted-foreground">{log.log_name}</p>
                                  </div>
                                )}
                              </div>
                              <div>
                                <label className="text-sm font-medium">Message</label>
                                <Textarea value={log.message} readOnly className="mt-1" rows={4} />
                              </div>
                              {log.raw && (
                                <div>
                                  <label className="text-sm font-medium">Raw Data</label>
                                  <Textarea value={JSON.stringify(log.raw, null, 2)}
                                    readOnly className="mt-1 font-mono text-xs" rows={6} />
                                </div>
                              )}
                            </div>
                          </ScrollArea>
                        </DialogContent>
                      </Dialog>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
